import React, { useState, useEffect, useCallback } from 'react';
import { getChemicalPricesPage, updateChemicalPrice, bulkDeleteChemicalPrices } from '../../api/priceCalculation';
import styles from './PriceCalculation.module.scss';
import { Edit, X, Save, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, Trash2 } from 'lucide-react';

export default function ChemicalPricesTab({ onError }) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ price_per_kg: '' });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const itemsPerPage = 20;

  const load = useCallback(async (page, search) => {
    setLoading(true);
    if (onError) onError(null);
    try {
      const skip = (page - 1) * itemsPerPage;
      const data = await getChemicalPricesPage({
        search: search || '',
        skip,
        limit: itemsPerPage,
      });
      setList(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total ?? 0);
    } catch (e) {
      if (onError) onError(e.message);
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // Debounce search input for server calls
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch when page or debounced search changes
  useEffect(() => {
    load(currentPage, debouncedSearch);
  }, [load, currentPage, debouncedSearch]);

  // Clear selection when page or search changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, debouncedSearch]);

  const deletableOnPage = list.filter((row) => row.price_per_kg != null);
  const pageDeletableIds = deletableOnPage.map((row) => row.chemical_id);
  const allPageSelected =
    pageDeletableIds.length > 0 && pageDeletableIds.every((id) => selectedIds.has(id));

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageDeletableIds.forEach((id) => next.delete(id));
      } else {
        pageDeletableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Delete ${ids.length} chemical price(s)? History will be kept; current prices will be cleared.`
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    if (onError) onError(null);
    try {
      await bulkDeleteChemicalPrices(ids);
      setSelectedIds(new Set());
      await load(currentPage, debouncedSearch);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      price_per_kg: row?.price_per_kg != null ? String(row.price_per_kg) : '',
    });
  };

  const handleSave = async () => {
    if (!editRow) return;
    const price = parseFloat(form.price_per_kg);
    if (isNaN(price) || price < 0) {
      if (onError) onError('Enter a valid price (≥ 0)');
      return;
    }
    setSaving(true);
    if (onError) onError(null);
    try {
      await updateChemicalPrice(editRow.chemical_id, {
        price_per_kg: price,
      });
      setEditRow(null);
      await load(currentPage, debouncedSearch);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const shownFrom = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const shownTo = Math.min(currentPage * itemsPerPage, total);

  return (
    <>
      {/* Search + bulk actions */}
      <div className={styles.toolbar}>
        <div className={styles.searchBar} style={{ marginBottom: 0, flex: 1, maxWidth: 500 }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={18} style={{ position: 'absolute', left: 12, color: 'var(--secondary-text, #6b7280)' }} />
            <input
              type="text"
              placeholder="Search chemicals by name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 40 }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--secondary-text, #6b7280)',
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        {selectedIds.size > 0 && (
          <button
            type="button"
            className={styles.bulkDeleteBtn}
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            title="Delete selected prices"
          >
            <Trash2 size={16} />
            {bulkDeleting ? 'Deleting…' : `Delete Selected (${selectedIds.size})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className={styles.loading}>Loading chemical prices…</div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      disabled={pageDeletableIds.length === 0}
                      onChange={toggleSelectPage}
                      title="Select all with prices on this page"
                      aria-label="Select all with prices on this page"
                    />
                  </th>
                  <th>Chemical</th>
                  <th>Price per kg</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={styles.empty}>
                      {debouncedSearch ? `No chemicals found matching "${debouncedSearch}"` : 'No chemicals with price data.'}
                    </td>
                  </tr>
                ) : (
                  list.map((row) => {
                    const canDelete = row.price_per_kg != null;
                    return (
                      <tr key={row.chemical_id}>
                        <td className={styles.checkboxCell}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.chemical_id)}
                            disabled={!canDelete}
                            onChange={() => toggleRow(row.chemical_id)}
                            title={canDelete ? 'Select for delete' : 'No price set'}
                            aria-label={`Select ${row.chemical_name || 'chemical'}`}
                          />
                        </td>
                        <td>{row.chemical_name ?? '—'}</td>
                        <td>{row.price_per_kg != null ? Number(row.price_per_kg).toFixed(2) : '—'}</td>
                        <td>
                          <div className={styles.actions}>
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              onClick={() => openEdit(row)}
                              title="Edit price"
                            >
                              <Edit size={14} /> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing {shownFrom} to {shownTo} of {total} chemicals
              </div>
              <div className={styles.paginationControls}>
            <button
              type="button"
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              type="button"
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            
            <div className={styles.pageNumbers}>
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }
                
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      type="button"
                      className={styles.pageNumber}
                      onClick={() => setCurrentPage(1)}
                    >
                      1
                    </button>
                  );
                  if (startPage > 2) {
                    pages.push(<span key="ellipsis1" style={{ padding: '0 4px' }}>...</span>);
                  }
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      type="button"
                      className={`${styles.pageNumber} ${currentPage === i ? styles.active : ''}`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(<span key="ellipsis2" style={{ padding: '0 4px' }}>...</span>);
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      type="button"
                      className={styles.pageNumber}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }
                
                return pages;
              })()}
            </div>
            
            <button
              type="button"
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
          )}
        </>
      )}

      {editRow && (
        <div className={styles.modalOverlay} onClick={() => setEditRow(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Edit price – {editRow.chemical_name}</h3>
              <button type="button" className={styles.btnSecondary} onClick={() => setEditRow(null)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Price per kg</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price_per_kg}
                  onChange={(e) => setForm((f) => ({ ...f, price_per_kg: e.target.value }))}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.btnSecondary} onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
